/**
 * Fuzzy label matching shared by every "Replace or create" prefill flow
 * (local file upload, library pick, Hugging Face import): maps a
 * file-extracted label onto a near-identical task label.
 */

/**
 * Matching key for label auto-mapping: unicode-normalize and strip accents,
 * lowercase, unify `_` / `-` / whitespace runs into single spaces, and trim
 * punctuation/symbols from the edges (quotes, trailing dots, brackets, …).
 * Non-Latin scripts pass through unchanged apart from case folding.
 */
export const normalizeLabel = (label) =>
    (label || '')
        .normalize('NFKD')
        .replace(/[\u0300-\u036f]/g, '')
        .toLowerCase()
        .replace(/[_\-\s]+/g, ' ')
        .replace(/^[\p{P}\p{S}]+|[\p{P}\p{S}]+$/gu, '')
        .trim();

/**
 * Fold simple English plurals onto the singular ("categories" -> "category",
 * "classes" -> "class", "labels" -> "label"). Words ending in "ss" / "us"
 * are left alone so "class" / "status" don't get mangled.
 */
export const foldPlural = (key) => {
    if (/[^aeiou\s]ies$/.test(key)) return `${key.slice(0, -3)}y`;
    if (/(?:[sxz]|ch|sh)es$/.test(key)) return key.slice(0, -2);
    if (/[^su]s$/.test(key)) return key.slice(0, -1);
    return key;
};

/**
 * Matcher that auto-maps a file-extracted label onto a task label when they
 * differ only by case / whitespace / separators / accents / edge punctuation,
 * or by a simple English plural. A plural match is skipped when two different
 * task labels fold onto the same singular (ambiguous — leave it to the user).
 * Returns the canonical task label, or undefined when there is no safe match.
 */
export const buildTaskLabelMatcher = (taskLabels) => {
    const byKey = new Map();
    /** foldedKey -> task label, or null when two task labels collide on it. */
    const byFoldedKey = new Map();
    for (const label of taskLabels || []) {
        const key = normalizeLabel(label);
        if (!key) continue;
        if (!byKey.has(key)) byKey.set(key, label);
        const foldedKey = foldPlural(key);
        if (!byFoldedKey.has(foldedKey)) byFoldedKey.set(foldedKey, label);
        else if (byFoldedKey.get(foldedKey) !== label) byFoldedKey.set(foldedKey, null);
    }
    return (fileLabel) => {
        const key = normalizeLabel(fileLabel);
        if (!key) return undefined;
        const direct = byKey.get(key);
        if (direct !== undefined) return direct;
        // Try the file label's singular against task keys directly first —
        // covers task singulars the folding heuristic would over-strip
        // (e.g. task "Gas" folds to "ga", but file "gases" folds to "gas").
        const foldedKey = foldPlural(key);
        return byKey.get(foldedKey) ?? byFoldedKey.get(foldedKey) ?? undefined;
    };
};
